import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { map, filter, take, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * Auth Guard to protect routes
 * Integrates with WiseAuth authentication system
 *
 * IMPORTANT: Waits for auth initialization to complete before checking
 * authentication status. This prevents race conditions on page refresh.
 */
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Wait for auth initialization to complete before checking authentication
  // This fixes the race condition where the guard would check isAuthenticated$
  // before the token refresh had a chance to complete
  return authService.authInitialized$.pipe(
    filter(initialized => initialized),
    take(1),
    switchMap(() => authService.isAuthenticated$),
    take(1),
    switchMap(isAuthenticated => {
      if (!isAuthenticated) {
        authService.setRedirectUrl(state.url);
        router.navigate(['/auth/login']);
        return of(false);
      }
      return authService.getCurrentUser().pipe(
        map(user => !authService.isOrganizationSuspended(user.organization_subscription_status)),
        catchError(() => of(true))
      );
    })
  );
};

